/**
 * The one host this bundle is allowed to report analytics from.
 *
 * Every client-side analytics SDK in the app (PostHog in `utils/analytics.ts`,
 * GA4 in `utils/ga4.ts`) asks the same question — "am I running on production?"
 * — and both destinations are `baa_covered: false`. Before this module they
 * asked it two different ways, with two different allowlists and OPPOSITE
 * fail modes, which is how one of them ends up silently rotting. See
 * `.claude/rules/analytics.md` and `.claude/rules/egress-policy.md`.
 *
 * Why the environment cannot answer it on its own: `preview-deploy.yml` builds
 * Firebase preview channels with `pnpm run build:prod` and sets no
 * `VITE_APP_ENV`, so `resolveEnvironment()` resolves `APP_ENV` to
 * `'production'` in a preview bundle exactly as it does in prod — and that same
 * workflow injects the production `VITE_POSTHOG_KEY` and points
 * `VITE_API_URL` at the production API. A preview channel is production-shaped
 * in every respect except the host it is served from. The host is the only
 * discriminator left.
 *
 * Deliberately NOT derived from `CORS_ORIGINS` or `VITE_FIREBASE_AUTH_DOMAIN`
 * in `config/secrets.yaml`, the only two places the repo names prod hosts.
 * `CORS_ORIGINS` exists to ADMIT the non-canonical hosts (`localhost:3000`,
 * `benenota-e6b50.web.app`, `…firebaseapp.com`) — exactly the traffic this
 * gate must reject — and the auth domain is a single Firebase setting, not a
 * host list. Reusing either would re-open the bug on the next edit to an
 * unrelated concern.
 */
export const PRODUCTION_HOST = 'app.bowerlabs.ai';

/**
 * True only when the document is being served from {@link PRODUCTION_HOST}.
 *
 * **Fails CLOSED.** No window, or any host that is not an exact match, is
 * treated as non-production. There is no "unknown" branch to widen: this reads
 * the document's own location rather than a property on an SDK payload, so
 * there is no shape an SDK can hand us that makes the answer unavailable.
 *
 * That property is the whole point. The earlier PostHog gate read
 * `properties.$host` off the outgoing event and failed OPEN when it was
 * absent — and posthog-js short-circuits property stamping for `$snapshot`, so
 * session-replay payloads (full rendered DOM) structurally never carry
 * `$host`. The one capture family carrying the most content was the one family
 * the gate always let through.
 *
 * Compares `location.host`, which includes a non-default port, so a
 * production-looking hostname served from some other port is not production.
 * On `app.bowerlabs.ai` (443) `host` and `hostname` are identical, so this is
 * the strictly tighter of the two readings at no cost.
 *
 * The decision is inherently sticky for the page: a document's host cannot
 * change without a navigation, so unlike an identity-keyed gate there is
 * nothing here that can re-open mid-session when a user signs out.
 */
export function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.host?.toLowerCase() === PRODUCTION_HOST;
}
